// src/pages/MeetingSchedulingConfig.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

/**
 * MeetingSchedulingConfig.jsx
 * Enterprise-grade (Zoho + Keka mixed UI)
 * Poppins + Roboto fonts
 *
 * Features:
 * - Meeting Types (1:1, Panel, HR Round, Manager Round, Custom)
 * - Slot Duration, Buffers, Max Meetings / Day
 * - Working Hours (Mon–Sun)
 * - Blackout Dates Calendar
 * - Auto-Assignment Rules (Round Robin / Load Balance)
 * - Host Pool (Employee List)
 * - Live Preview for generated time-slots
 * - Import / Export JSON
 * - LocalStorage persistence (key: meeting_sched_v1)
 * - Compact Modal (max-h, not covering header)
 */

const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&family=Roboto:wght@300;400;500;700&display=swap');
`;

const LS_KEY = "meeting_sched_v1";
const uid = (p = "id") =>
  `${p}_${Date.now().toString(36)}_${Math.floor(Math.random() * 99999)}`;

/* ---------------------------- Modal ---------------------------- */
const Modal = ({ open, title, children, width = "max-w-3xl", onClose }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] flex items-start justify-center pt-20 px-4"
        style={{ background: "rgba(10,11,13,0.35)" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ scale: 0.96, y: -8 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: -8 }}
          className={`bg-white rounded-2xl shadow-2xl w-full ${width} max-h-[70vh] overflow-auto`}
        >
          <div className="sticky top-0 bg-white border-b px-5 py-3 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <button
              onClick={onClose}
              className="px-3 py-1 rounded hover:bg-slate-100"
            >
              ✕
            </button>
          </div>
          <div className="p-6">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

/* ---------------------------- Default Config ---------------------------- */
const DEFAULT_CONFIG = {
  meetingTypes: [
    { id: uid("mt"), name: "HR Screening", duration: 30, buffer: 5 },
    { id: uid("mt"), name: "Manager Interview", duration: 45, buffer: 10 },
    { id: uid("mt"), name: "Panel Discussion", duration: 60, buffer: 10 },
  ],

  workingHours: {
    mon: { start: "09:00", end: "18:00", enabled: true },
    tue: { start: "09:00", end: "18:00", enabled: true },
    wed: { start: "09:00", end: "18:00", enabled: true },
    thu: { start: "09:00", end: "18:00", enabled: true },
    fri: { start: "09:00", end: "18:00", enabled: true },
    sat: { start: "10:00", end: "14:00", enabled: false },
    sun: { start: "00:00", end: "00:00", enabled: false },
  },

  maxMeetingsPerDay: 6,

  blackoutDates: [],

  hostPool: [
    { id: "E001", name: "Aman Sharma" },
    { id: "E002", name: "Priya Nair" },
    { id: "E003", name: "Vikas Mehta" },
  ],

  autoAssignmentRule: "round_robin", // round_robin | load_balance
};

/* ---------------------------- Utility ---------------------------- */
const WEEKDAYS = [
  ["mon", "Mon"],
  ["tue", "Tue"],
  ["wed", "Wed"],
  ["thu", "Thu"],
  ["fri", "Fri"],
  ["sat", "Sat"],
  ["sun", "Sun"],
];

/* Generate slots preview */
const generateSlots = (duration, buffer, wh) => {
  if (!wh?.enabled) return [];

  const toMinutes = (t) => {
    const [h, m] = t.split(":");
    return Number(h) * 60 + Number(m);
  };

  const start = toMinutes(wh.start);
  const end = toMinutes(wh.end);

  const slots = [];
  let cur = start;

  while (cur + duration <= end) {
    const h = String(Math.floor(cur / 60)).padStart(2, "0");
    const m = String(cur % 60).padStart(2, "0");
    slots.push(`${h}:${m}`);
    cur += duration + buffer;
  }

  return slots;
};

/* ---------------------------- Main Component ---------------------------- */
export default function MeetingSchedulingConfig() {
  const [cfg, setCfg] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : DEFAULT_CONFIG;
    } catch {
      return DEFAULT_CONFIG;
    }
  });

  const [toast, setToast] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editor, setEditor] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(cfg));
  }, [cfg]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 1500);
    return () => clearTimeout(t);
  }, [toast]);

  const showToast = (msg) => setToast(msg);

  /* -------------------- Meeting Type CRUD -------------------- */

  const openNewType = () => {
    setEditor({
      id: uid("mt"),
      name: "",
      duration: 30,
      buffer: 5,
    });
    setIsModalOpen(true);
  };

  const editType = (mt) => {
    setEditor(JSON.parse(JSON.stringify(mt)));
    setIsModalOpen(true);
  };

  const saveType = (t) => {
    if (!t.name.trim()) return alert("Name required.");

    setCfg((p) => {
      const idx = p.meetingTypes.findIndex((x) => x.id === t.id);
      if (idx === -1)
        return { ...p, meetingTypes: [...p.meetingTypes, t] };

      const arr = [...p.meetingTypes];
      arr[idx] = t;
      return { ...p, meetingTypes: arr };
    });

    showToast("Saved");
    setIsModalOpen(false);
  };

  const deleteType = (id) => {
    if (!window.confirm("Delete meeting type?")) return;
    setCfg((p) => ({
      ...p,
      meetingTypes: p.meetingTypes.filter((x) => x.id !== id),
    }));
    showToast("Deleted");
  };

  /* -------------------- Blackout Dates -------------------- */
  const addBlackout = (date) => {
    if (!date) return;
    setCfg((p) => ({
      ...p,
      blackoutDates: [...p.blackoutDates, date],
    }));
  };

  const removeBlackout = (d) => {
    setCfg((p) => ({
      ...p,
      blackoutDates: p.blackoutDates.filter((x) => x !== d),
    }));
  };

  /* -------------------- Import / Export -------------------- */
  const exportConfig = () => {
    const blob = new Blob([JSON.stringify(cfg, null, 2)], {
      type: "application/json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "meeting_scheduling_config.json";
    a.click();
  };

  const importConfig = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        setCfg(data);
        showToast("Imported");
      } catch {
        alert("Invalid JSON");
      }
    };
    reader.readAsText(file);
  };

  /* -------------------- Editor Modal -------------------- */
  const Editor = () => {
    if (!editor) return null;
    const t = editor;

    return (
      <div className="space-y-4">
        <div>
          <label className="text-xs text-slate-600">Name</label>
          <input
            value={t.name}
            onChange={(e) => setEditor({ ...t, name: e.target.value })}
            className="w-full px-3 py-2 border rounded"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-600">Duration (mins)</label>
            <input
              type="number"
              value={t.duration}
              onChange={(e) =>
                setEditor({ ...t, duration: Number(e.target.value) })
              }
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Buffer (mins)</label>
            <input
              type="number"
              value={t.buffer}
              onChange={(e) =>
                setEditor({ ...t, buffer: Number(e.target.value) })
              }
              className="w-full px-3 py-2 border rounded"
            />
          </div>
        </div>

        {/* Slot preview */}
        <div className="border rounded p-3">
          <div className="text-sm font-medium text-slate-800">
            Slot Preview (for Monday)
          </div>
          <div className="text-xs text-slate-500 mb-2">
            Based on working hours
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            {generateSlots(
              t.duration,
              t.buffer,
              cfg.workingHours.mon
            ).map((s, i) => (
              <span
                key={i}
                className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded"
              >
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <button
            onClick={() => {
              setIsModalOpen(false);
              setEditor(null);
            }}
            className="px-4 py-2 border rounded"
          >
            Cancel
          </button>
          <button
            onClick={() => saveType(t)}
            className="px-4 py-2 bg-indigo-600 text-white rounded"
          >
            Save
          </button>
        </div>
      </div>
    );
  };

  /* ---------------------------- UI ---------------------------- */
  return (
    <div
      className="min-h-screen bg-slate-50 p-6"
      style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
    >
      <style>{FONT_IMPORT}</style>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className="fixed top-6 right-6 bg-indigo-600 text-white px-4 py-2 rounded shadow"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <header className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">
              Meeting Scheduling Configuration
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Configure meeting types, time-slots, blackout dates, and host
              assignment rules.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={exportConfig}
              className="px-4 py-2 border rounded bg-white"
            >
              Export
            </button>
            <label className="px-4 py-2 border rounded bg-white cursor-pointer">
              Import
              <input
                type="file"
                accept=".json"
                className="hidden"
                ref={fileRef}
                onChange={(e) =>
                  e.target.files?.[0] &&
                  importConfig(e.target.files[0])
                }
              />
            </label>
          </div>
        </header>

        {/* Meeting Types */}
        <section className="bg-white p-5 rounded-xl shadow">
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">
              Meeting Types
            </h2>
            <button
              onClick={openNewType}
              className="px-4 py-2 bg-indigo-600 text-white rounded"
            >
              + Add Type
            </button>
          </div>

          {cfg.meetingTypes.length === 0 ? (
            <div className="text-slate-500 text-sm">No types added.</div>
          ) : (
            <div className="space-y-3">
              {cfg.meetingTypes.map((mt) => (
                <div
                  key={mt.id}
                  className="p-4 border rounded-xl bg-slate-50 flex justify-between items-start"
                >
                  <div>
                    <div className="text-sm font-semibold">{mt.name}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Duration: {mt.duration} mins · Buffer: {mt.buffer} mins
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => editType(mt)}
                      className="px-3 py-1 border rounded bg-white"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteType(mt.id)}
                      className="px-3 py-1 border rounded bg-rose-50 text-rose-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Working Hours */}
        <section className="bg-white p-5 rounded-xl shadow">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            Working Hours
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {WEEKDAYS.map(([key, label]) => {
              const w = cfg.workingHours[key];
              return (
                <div
                  key={key}
                  className="border p-3 rounded-xl bg-slate-50"
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="font-medium text-sm">{label}</div>
                    <input
                      type="checkbox"
                      checked={w.enabled}
                      onChange={(e) =>
                        setCfg({
                          ...cfg,
                          workingHours: {
                            ...cfg.workingHours,
                            [key]: {
                              ...w,
                              enabled: e.target.checked,
                            },
                          },
                        })
                      }
                    />
                  </div>

                  {w.enabled && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-slate-600">
                          Start
                        </label>
                        <input
                          type="time"
                          value={w.start}
                          onChange={(e) =>
                            setCfg({
                              ...cfg,
                              workingHours: {
                                ...cfg.workingHours,
                                [key]: {
                                  ...w,
                                  start: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full px-2 py-1 border rounded"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-600">
                          End
                        </label>
                        <input
                          type="time"
                          value={w.end}
                          onChange={(e) =>
                            setCfg({
                              ...cfg,
                              workingHours: {
                                ...cfg.workingHours,
                                [key]: {
                                  ...w,
                                  end: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full px-2 py-1 border rounded"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Blackout Dates */}
        <section className="bg-white p-5 rounded-xl shadow">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            Blackout Dates
          </h2>

          <div className="flex gap-3 mb-3">
            <input
              type="date"
              onChange={(e) => addBlackout(e.target.value)}
              className="px-3 py-2 border rounded"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {cfg.blackoutDates.length === 0 && (
              <div className="text-slate-500 text-sm">
                No blackout dates defined.
              </div>
            )}

            {cfg.blackoutDates.map((d) => (
              <div
                key={d}
                className="px-3 py-1 text-sm bg-rose-100 text-rose-700 rounded flex items-center gap-2"
              >
                {d}
                <span
                  className="cursor-pointer"
                  onClick={() => removeBlackout(d)}
                >
                  ✕
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Auto Assignment */}
        <section className="bg-white p-5 rounded-xl shadow">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            Auto Assignment Rule
          </h2>

          <select
            value={cfg.autoAssignmentRule}
            onChange={(e) =>
              setCfg({ ...cfg, autoAssignmentRule: e.target.value })
            }
            className="px-3 py-2 border rounded"
          >
            <option value="round_robin">Round Robin</option>
            <option value="load_balance">Load Balance</option>
          </select>
        </section>

        {/* Host Pool */}
        <section className="bg-white p-5 rounded-xl shadow">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            Host Pool (Eligible Interviewers)
          </h2>

          <ul className="space-y-2">
            {cfg.hostPool.map((h) => (
              <li
                key={h.id}
                className="p-3 bg-slate-50 border rounded flex justify-between items-center"
              >
                <div className="text-sm">{h.name}</div>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Modal */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editor ? editor.name || "Meeting Type" : ""}
      >
        {editor && <Editor />}
      </Modal>
    </div>
  );
}
